import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BISocial } from "@/components/BISocial";
import { BIWeb } from "@/components/BIWeb";
import { BIEmail } from "@/components/BIEmail";
import {
  ShieldAlert, Users, Megaphone, ScrollText, FileText, LayoutDashboard,
  Search, Download, LogIn, Trash2, Save, TrendingUp, Crown, Plus, X,
  Globe, Upload, ImageIcon, ExternalLink, AlertTriangle, EyeOff, Mail, RefreshCw, Check, BarChart3,
  CalendarClock, BellRing,
} from "lucide-react";

const GOLD = "hsl(var(--gold))";
const PLAN_OPTIONS = ["free", "starter", "executive", "premium", "founding"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      {Icon && <Icon size={16} className="text-[hsl(var(--gold))]" />}
    </div>
    <p className="mt-2 font-display text-3xl tabular-nums">{value}</p>
  </Card>
);

/* ---------------- Overview ---------------- */
function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/superadmin/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="h-40 animate-pulse rounded-[var(--radius)] bg-secondary/60" />;
  const plans = Object.entries(d.plan_distribution || {}).sort((a, b) => b[1] - a[1]);
  const maxPlan = Math.max(1, ...plans.map(([, v]) => v));
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Users" value={d.total_users} icon={Users} testid="sa-stat-total" />
        <Stat label="Paid Subscribers" value={d.paid_subscribers} icon={Crown} testid="sa-stat-paid" />
        <Stat label="New (7 days)" value={d.new_7d} icon={TrendingUp} />
        <Stat label="New (30 days)" value={d.new_30d} icon={TrendingUp} />
        <Stat label="Active" value={d.active_users} />
        <Stat label="Verified" value={d.verified} />
        <Stat label="Admins" value={d.admins} />
        <Stat label="Day Plans" value={d.day_plans_total} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
          <h3 className="font-display text-xl">Plan Distribution</h3>
          <div className="mt-4 space-y-3">
            {plans.length === 0 && <p className="text-sm text-muted-foreground italic">No users yet.</p>}
            {plans.map(([plan, count]) => (
              <div key={plan} data-testid={`sa-plan-${plan}`}>
                <div className="flex items-center justify-between text-sm"><span className="capitalize">{plan}</span><span className="tabular-nums text-muted-foreground">{count}</span></div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full" style={{ width: `${(count / maxPlan) * 100}%`, backgroundColor: GOLD }} /></div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
          <h3 className="font-display text-xl">Recent Signups</h3>
          <ul className="mt-4 space-y-2">
            {(d.recent_signups || []).length === 0 && <li className="text-sm text-muted-foreground italic">No signups yet.</li>}
            {(d.recent_signups || []).map((u) => (
              <li key={u.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                <div className="min-w-0"><p className="truncate text-sm font-medium">{u.name || "—"}</p><p className="truncate text-xs text-muted-foreground">{u.email}</p></div>
                <div className="flex items-center gap-2 shrink-0"><Badge variant="secondary" className="capitalize">{u.plan}</Badge><span className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</span></div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Subscribers ---------------- */
function Subscribers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [planF, setPlanF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sel, setSel] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q; if (planF) params.plan = planF; if (statusF) params.status = statusF;
    api.get("/admin/users", { params }).then(({ data }) => setUsers(data.users || [])).catch(() => {});
  }, [q, planF, statusF]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openUser = async (u) => {
    setSel(u); setSummary(null);
    try { const { data } = await api.get(`/superadmin/users/${u.id}/summary`); setSummary(data); } catch { /* ignore */ }
  };
  const patchUser = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success("Subscriber updated."); load();
      setSel((s) => (s && s.id === id ? { ...s, ...body } : s)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Subscriber deleted."); setSel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };
  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/superadmin/impersonate/${u.id}`);
      localStorage.setItem("vb_admin_token", localStorage.getItem("vb_token"));
      localStorage.setItem("vb_impersonate_name", u.name || u.email);
      localStorage.setItem("vb_token", data.token);
      window.location.href = "/dashboard";
    } catch { toast.error("Could not start impersonation."); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/superadmin/users.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-52">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="rounded-xl pl-9" data-testid="sa-user-search" />
        </div>
        <select value={planF} onChange={(e) => setPlanF(e.target.value)} className="h-10 rounded-xl border border-border bg-card px-3 text-sm" data-testid="sa-filter-plan">
          <option value="">All plans</option>
          {PLAN_OPTIONS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
        </select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-10 rounded-xl border border-border bg-card px-3 text-sm" data-testid="sa-filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid="sa-export-csv"><Download size={15} className="mr-1.5" />Export CSV</Button>
      </div>

      <Card className="overflow-hidden border-border shadow-[var(--shadow-sm)]">
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-secondary/60 backdrop-blur">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground italic">No subscribers found.</td></tr>}
              {users.map((u) => (
                <tr key={u.id} onClick={() => openUser(u)} className="cursor-pointer border-t border-border transition-colors hover:bg-secondary/40" data-testid={`sa-user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name || "—"}{u.is_master && <Crown size={12} className="ml-1.5 inline text-[hsl(var(--gold))]" />}{u.is_admin && !u.is_master && <ShieldAlert size={12} className="ml-1.5 inline text-muted-foreground" />}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="capitalize">{u.plan || "free"}</Badge></td>
                  <td className="px-4 py-3"><Badge variant={u.status === "disabled" ? "outline" : "secondary"} className={u.status === "disabled" ? "text-destructive" : ""}>{u.status || "active"}</Badge></td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-lg">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">{sel.name || sel.email}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{sel.email}</p>
                {summary && (
                  <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-secondary/30 p-3 text-xs" data-testid="sa-user-summary">
                    <div>Archetype: <span className="font-medium capitalize">{summary.stats.archetype}</span></div>
                    <div>Day plans: <span className="font-medium tabular-nums">{summary.stats.day_plans}</span></div>
                    <div>Sleep logs: <span className="font-medium tabular-nums">{summary.stats.sleep_logs}</span></div>
                    <div>Calendar items: <span className="font-medium tabular-nums">{summary.stats.calendar_items}</span></div>
                    <div className="col-span-2">Last active: <span className="font-medium">{fmtDate(summary.stats.last_active_date)}</span></div>
                    <div className="col-span-2">Departments: <span className="font-medium">{(summary.stats.departments || []).join(", ") || "—"}</span></div>
                  </div>
                )}
                <div>
                  <label className="text-xs text-muted-foreground">Plan</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {PLAN_OPTIONS.map((p) => (
                      <button key={p} onClick={() => patchUser(sel.id, { plan: p })} className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${(sel.plan || "free") === p ? "border-transparent bg-[hsl(var(--gold))] text-primary-foreground" : "border-border hover:border-[hsl(var(--gold))]"}`} data-testid={`sa-set-plan-${p}`}>{p}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                  <div><p className="text-sm font-medium">Account active</p><p className="text-xs text-muted-foreground">Disabled users can't sign in.</p></div>
                  <Switch checked={(sel.status || "active") === "active"} onCheckedChange={(v) => patchUser(sel.id, { status: v ? "active" : "disabled" })} disabled={sel.is_master} data-testid="sa-toggle-status" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                  <div><p className="text-sm font-medium">Admin access</p><p className="text-xs text-muted-foreground">Grants the standard admin center.</p></div>
                  <Switch checked={!!sel.is_admin} onCheckedChange={(v) => patchUser(sel.id, { is_admin: v })} disabled={sel.is_master} data-testid="sa-toggle-admin" />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:justify-between">
                {!sel.is_master ? (
                  <Button variant="ghost" className="rounded-xl text-destructive hover:text-destructive" onClick={() => del(sel.id)} data-testid="sa-delete-user"><Trash2 size={15} className="mr-1.5" />Delete</Button>
                ) : <span />}
                <Button className="rounded-xl" onClick={() => impersonate(sel)} data-testid="sa-impersonate"><LogIn size={15} className="mr-1.5" />Log in as this user</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Announcement ---------------- */
function Announcement() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/superadmin/announcement").then(({ data }) => setA(data)).catch(() => setA({ message: "", active: false, variant: "info" })); }, []);
  if (!a) return <div className="h-40 animate-pulse rounded-[var(--radius)] bg-secondary/60" />;
  const save = async () => {
    try { await api.put("/superadmin/announcement", { message: a.message, active: a.active, variant: a.variant }); toast.success("Announcement saved."); }
    catch { toast.error("Could not save."); }
  };
  const VARS = [["info", "Info"], ["success", "Success"], ["warning", "Warning"]];
  return (
    <Card className="max-w-2xl p-6 border-border shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2"><Megaphone size={18} className="text-[hsl(var(--gold))]" /><h3 className="font-display text-xl">Broadcast Announcement</h3></div>
      <p className="mt-1 text-sm text-muted-foreground">Shows as a banner to every signed-in user until you turn it off.</p>
      <div className="mt-4 space-y-4">
        <Textarea value={a.message} onChange={(e) => setA({ ...a, message: e.target.value })} placeholder="e.g. New: sleep tracking is live in Health → Sleep & Energy." className="rounded-xl min-h-20" data-testid="sa-announce-message" />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Tone:</span>
          {VARS.map(([v, l]) => (
            <button key={v} onClick={() => setA({ ...a, variant: v })} className={`rounded-full border px-3 py-1 text-xs transition-colors ${a.variant === v ? "border-transparent bg-[hsl(var(--gold))] text-primary-foreground" : "border-border hover:border-[hsl(var(--gold))]"}`} data-testid={`sa-announce-variant-${v}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
          <div><p className="text-sm font-medium">Show to all users</p><p className="text-xs text-muted-foreground">Toggle the live banner on/off.</p></div>
          <Switch checked={a.active} onCheckedChange={(v) => setA({ ...a, active: v })} data-testid="sa-announce-active" />
        </div>
        <Button onClick={save} className="rounded-xl" data-testid="sa-announce-save"><Save size={15} className="mr-1.5" />Save Announcement</Button>
      </div>
    </Card>
  );
}

/* ---------------- Audit ---------------- */
function Audit() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/admin/audit", { params: { limit: 200 } }).then(({ data }) => setEvents(data.events || [])).catch(() => {}); }, []);
  return (
    <Card className="overflow-hidden border-border shadow-[var(--shadow-sm)]">
      <div className="max-h-[600px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-secondary/60 backdrop-blur"><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground"><th className="px-4 py-3">Event</th><th className="px-4 py-3">Detail</th><th className="px-4 py-3">When</th></tr></thead>
          <tbody>
            {events.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground italic">No events logged.</td></tr>}
            {events.map((e, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-4 py-2.5"><Badge variant="secondary" className="font-mono text-[11px]">{e.type}</Badge></td>
                <td className="px-4 py-2.5 text-muted-foreground">{e.meta ? JSON.stringify(e.meta) : "—"}</td>
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------------- Content (Journal + Pricing) ---------------- */
function Content() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const loadJournal = useCallback(() => api.get("/admin/content/journal").then(({ data }) => setPosts(data.posts || [])).catch(() => {}), []);
  useEffect(() => { loadJournal(); }, [loadJournal]);

  const blank = { title: "", category: "", excerpt: "", body: "", read: "4 min", published: true };
  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/content/journal/${editing.id}`, editing);
      else await api.post("/admin/content/journal", editing);
      toast.success("Saved."); setEditing(null); loadJournal();
    } catch { toast.error("Could not save post."); }
  };
  const del = async (id) => { try { await api.delete(`/admin/content/journal/${id}`); toast.success("Deleted."); loadJournal(); } catch { toast.error("Delete failed."); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">Journal Posts</h3>
        <Button className="rounded-xl" onClick={() => setEditing({ ...blank })} data-testid="sa-journal-new"><Plus size={15} className="mr-1.5" />New Post</Button>
      </div>
      <div className="grid gap-3">
        {posts.length === 0 && <p className="text-sm text-muted-foreground italic">No posts yet.</p>}
        {posts.map((p) => (
          <Card key={p.id} className="flex items-center justify-between gap-3 p-4 border-border">
            <div className="min-w-0"><p className="truncate font-medium">{p.title}</p><p className="truncate text-xs text-muted-foreground">{p.category} · {p.published ? "Published" : "Draft"}</p></div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setEditing(p)} data-testid={`sa-journal-edit-${p.id}`}>Edit</Button>
              <Button size="sm" variant="ghost" className="rounded-xl text-destructive" onClick={() => del(p.id)}><Trash2 size={14} /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">{editing.id ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-xl" data-testid="sa-journal-title" />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Category" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl" />
                  <Input placeholder="Read time (e.g. 4 min)" value={editing.read || ""} onChange={(e) => setEditing({ ...editing, read: e.target.value })} className="rounded-xl" />
                </div>
                <Textarea placeholder="Excerpt" value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="rounded-xl min-h-16" />
                <Textarea placeholder="Body" value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="rounded-xl min-h-32" />
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                  <p className="text-sm font-medium">Published</p>
                  <Switch checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="rounded-xl" data-testid="sa-journal-save"><Save size={15} className="mr-1.5" />Save</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Newsletter subscribers ---------------- */
const SOURCE_LABELS = { "sunday-reset": "Sunday Reset", "life-audit": "Life Audit", "home": "Home" };
const sourceLabel = (s) => SOURCE_LABELS[s] || (s || "—");

const STATUS_LABELS = { subscribed: "Subscribed", unsubscribed: "Unsubscribed", bounced: "Bounced", spam: "Spam", unconfirmed: "Unconfirmed" };
const statusLabel = (s) => STATUS_LABELS[s] || (s || "Subscribed");
const StatusBadge = ({ status }) => {
  const s = status || "subscribed";
  const danger = s === "bounced" || s === "spam";
  const off = s === "unsubscribed";
  return (
    <Badge variant={danger ? "outline" : "secondary"} className={danger ? "text-destructive" : off ? "text-muted-foreground" : ""}>
      {statusLabel(s)}
    </Badge>
  );
};

function NewsletterSubs() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sourceF, setSourceF] = useState("");
  const [hook, setHook] = useState(null);
  const [savingHook, setSavingHook] = useState(false);

  const loadHook = useCallback(() => {
    api.get("/superadmin/newsletter/webhook").then(({ data }) => setHook(data)).catch(() => setHook({ mailerlite_configured: false, configured: false }));
  }, []);
  useEffect(() => { loadHook(); }, [loadHook]);

  const enableSync = async () => {
    setSavingHook(true);
    try {
      const { data } = await api.post("/superadmin/newsletter/webhook", { base_url: BACKEND });
      setHook({ mailerlite_configured: true, ...data });
      toast.success("Unsubscribe & bounce sync is live.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enable sync.");
    } finally { setSavingHook(false); }
  };

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (sourceF) params.source = sourceF;
    api.get("/superadmin/newsletter", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [q, sourceF]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/superadmin/newsletter.csv", { params: sourceF ? { source: sourceF } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "newsletter_subscribers.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  if (!data) return <div className="h-40 animate-pulse rounded-[var(--radius)] bg-secondary/60" />;
  const bySource = data.by_source || {};
  const subs = data.subscribers || [];

  return (
    <div className="space-y-5" data-testid="newsletter-manager">
      {/* Unsubscribe & bounce sync */}
      <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid="nl-sync-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--gold)/0.12)]">
              <RefreshCw size={18} className="text-[hsl(var(--gold))]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg">Unsubscribe &amp; bounce sync</h3>
                {hook?.configured ? (
                  <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]" data-testid="nl-sync-status"><Check size={12} className="mr-1" />Connected</Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground" data-testid="nl-sync-status">Not set up</Badge>
                )}
              </div>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Keeps this list in step with MailerLite — when someone unsubscribes, bounces, or reports spam, their status here updates automatically.
              </p>
              {!hook?.mailerlite_configured && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive"><AlertTriangle size={13} /> MailerLite isn't connected yet.</p>
              )}
            </div>
          </div>
          <Button onClick={enableSync} disabled={savingHook || !hook?.mailerlite_configured} variant={hook?.configured ? "outline" : "default"} className="rounded-xl shrink-0" data-testid="nl-sync-enable">
            <RefreshCw size={15} className="mr-1.5" />{savingHook ? "Working…" : hook?.configured ? "Refresh sync" : "Enable sync"}
          </Button>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Subscribers" value={data.total} icon={Mail} testid="nl-stat-total" />
        <Stat label="Sunday Reset" value={bySource["sunday-reset"] || 0} testid="nl-stat-sunday-reset" />
        <Stat label="Life Audit" value={bySource["life-audit"] || 0} testid="nl-stat-life-audit" />
        <Stat label="Home" value={bySource["home"] || 0} testid="nl-stat-home" />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-52">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="rounded-xl pl-9" data-testid="nl-search" />
        </div>
        <select value={sourceF} onChange={(e) => setSourceF(e.target.value)} className="h-10 rounded-xl border border-border bg-card px-3 text-sm" data-testid="nl-filter-source">
          <option value="">All sources</option>
          <option value="sunday-reset">Sunday Reset</option>
          <option value="life-audit">Life Audit</option>
          <option value="home">Home</option>
        </select>
        <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid="nl-export-csv"><Download size={15} className="mr-1.5" />Export CSV</Button>
      </div>

      {/* Table */}
      <Card className="overflow-hidden border-border shadow-[var(--shadow-sm)]">
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-secondary/60 backdrop-blur">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Audit Score</th>
                <th className="px-4 py-3">Tender Pillar</th>
                <th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground italic">No subscribers yet.</td></tr>}
              {subs.map((s) => (
                <tr key={s.id || s.email} className="border-t border-border transition-colors hover:bg-secondary/40" data-testid={`nl-row-${s.email}`}>
                  <td className="px-4 py-3 font-medium">{s.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.email}</td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className={s.source === "life-audit" ? "border border-[hsl(var(--gold))] text-[hsl(var(--gold))]" : ""}>{sourceLabel(s.source)}</Badge>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{typeof s.audit_score === "number" ? `${s.audit_score} / 100` : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.audit_tender_pillar || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- SEO & Sharing ---------------- */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEO_PAGES = [
  { slug: "_default", label: "Global Default", hint: "Fallback used when a page leaves a field blank." },
  { slug: "home", label: "Home", hint: "The public brand landing at /" },
  { slug: "sunday-reset", label: "Sunday Reset", hint: "Newsletter landing at /sunday-reset" },
  { slug: "life-audit", label: "Life Audit", hint: "Free assessment at /life-audit" },
];

const TITLE_MIN = 30, TITLE_MAX = 60;
const DESC_MIN = 70, DESC_MAX = 160;

const blankForm = () => ({ title: "", description: "", social_title: "", social_description: "", image_id: "", image_alt: "", noindex: false });

const Counter = ({ value, min, max }) => {
  const len = (value || "").length;
  const ok = len >= min && len <= max;
  const over = len > max;
  const tone = over ? "text-destructive" : ok ? "text-[hsl(var(--gold))]" : "text-muted-foreground";
  return (
    <span className={`text-[11px] tabular-nums ${tone}`} data-testid="seo-counter">
      {len} / {max}{over ? " · too long" : len < min && len > 0 ? " · a little short" : ""}
    </span>
  );
};

function SeoManager() {
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState("home");
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/superadmin/seo").then(({ data }) => setData(data)).catch(() => toast.error("Could not load SEO settings."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentPage = useMemo(() => (data?.pages || []).find((p) => p.slug === slug), [data, slug]);
  const defaultRaw = useMemo(() => ((data?.pages || []).find((p) => p.slug === "_default")?.raw) || {}, [data]);

  // Load the form when the page or data changes.
  useEffect(() => {
    if (currentPage) setForm({ ...blankForm(), ...currentPage.raw });
  }, [currentPage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Effective (merged) preview values — page value wins, else default, else page's own merged fallback.
  const eff = (field) => {
    const v = (form[field] || "").trim();
    if (v) return v;
    if (slug !== "_default") { const d = (defaultRaw[field] || "").trim(); if (d) return d; }
    return (currentPage?.merged?.[field] || "").trim();
  };
  const effTitle = eff("title") || "Intrinsic Daily";
  const effDesc = eff("description");
  const effSocialTitle = (form.social_title || "").trim() || (slug !== "_default" ? (defaultRaw.social_title || "").trim() : "") || effTitle;
  const effSocialDesc = (form.social_description || "").trim() || (slug !== "_default" ? (defaultRaw.social_description || "").trim() : "") || effDesc;

  const imageId = (form.image_id || "").trim() || (slug !== "_default" ? (defaultRaw.image_id || "").trim() : "");
  const imagePreview = imageId ? `${BACKEND}/api/seo/media/${imageId}` : "";
  const canonical = currentPage?.merged?.canonical || "https://intrinsicdaily.com/";
  const domain = (data?.site_url || "https://intrinsicdaily.com").replace(/^https?:\/\//, "");

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/superadmin/seo/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_id", res.image_id);
      toast.success("Share image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/seo/${slug}`, form);
      toast.success("SEO saved. It's live on the site.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="h-40 animate-pulse rounded-[var(--radius)] bg-secondary/60" />;

  return (
    <div className="space-y-6" data-testid="seo-manager">
      {/* Page selector */}
      <div className="flex flex-wrap gap-2">
        {SEO_PAGES.map((p) => (
          <button
            key={p.slug}
            onClick={() => setSlug(p.slug)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${slug === p.slug ? "border-transparent bg-[hsl(var(--gold))] text-primary-foreground" : "border-border hover:border-[hsl(var(--gold))]"}`}
            data-testid={`seo-page-${p.slug}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Editor */}
        <Card className="p-6 border-border shadow-[var(--shadow-sm)] space-y-5">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-[hsl(var(--gold))]" />
            <h3 className="font-display text-xl">{SEO_PAGES.find((p) => p.slug === slug)?.label}</h3>
          </div>
          <p className="text-sm text-muted-foreground -mt-2">{SEO_PAGES.find((p) => p.slug === slug)?.hint}</p>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Page title (SEO)</label>
              <Counter value={form.title} min={TITLE_MIN} max={TITLE_MAX} />
            </div>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={slug === "_default" ? "Site-wide title" : "Leave blank to inherit the default"} className="rounded-xl" data-testid="seo-title" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Meta description</label>
              <Counter value={form.description} min={DESC_MIN} max={DESC_MAX} />
            </div>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="One or two clear sentences that describe this page." className="rounded-xl min-h-20" data-testid="seo-description" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Social title</label>
              <Input value={form.social_title} onChange={(e) => set("social_title", e.target.value)} placeholder="Defaults to page title" className="rounded-xl" data-testid="seo-social-title" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Image alt text</label>
              <Input value={form.image_alt} onChange={(e) => set("image_alt", e.target.value)} placeholder="Describe the share image" className="rounded-xl" data-testid="seo-image-alt" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Social description</label>
            <Textarea value={form.social_description} onChange={(e) => set("social_description", e.target.value)} placeholder="Defaults to the meta description." className="rounded-xl min-h-16" data-testid="seo-social-description" />
          </div>

          {/* Share image */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Share image (1200 × 630 recommended)</label>
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary/40">
                {imagePreview ? (
                  <img src={imagePreview} alt="Share preview" className="h-full w-full object-cover" data-testid="seo-image-thumb" />
                ) : (
                  <ImageIcon size={20} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm transition-colors hover:border-[hsl(var(--gold))]" data-testid="seo-upload-label">
                  <Upload size={15} />{uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="seo-upload-input" />
                </label>
                {form.image_id && (
                  <button onClick={() => set("image_id", "")} className="inline-flex items-center gap-1.5 text-xs text-destructive" data-testid="seo-remove-image"><X size={13} />Remove</button>
                )}
              </div>
            </div>
          </div>

          {/* Index control */}
          <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
            <div className="flex items-center gap-2">
              <EyeOff size={16} className="text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Hide from search engines</p>
                <p className="text-xs text-muted-foreground">Turn on to add noindex. Keep off for public pages.</p>
              </div>
            </div>
            <Switch checked={!!form.noindex} onCheckedChange={(v) => set("noindex", v)} data-testid="seo-noindex" />
          </div>

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-muted-foreground">
              {currentPage?.raw?.updated_at ? `Last saved ${fmtDate(currentPage.raw.updated_at)}` : "Not saved yet"}
            </p>
            <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="seo-save"><Save size={15} className="mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
          </div>
        </Card>

        {/* Live previews */}
        <div className="space-y-6">
          <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
            <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Google result preview</h4>
            <div className="mt-4 space-y-1" data-testid="seo-google-preview">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="truncate">{domain}{canonical.replace(/^https?:\/\/[^/]+/, "")}</span>
              </div>
              <p className="text-[19px] leading-snug text-[#1a0dab] line-clamp-1">{effTitle}</p>
              <p className="text-[13px] leading-snug text-[#4d5156] line-clamp-2">{effDesc || "Add a meta description to control this snippet."}</p>
            </div>
            {form.noindex && (
              <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
                <AlertTriangle size={13} className="text-destructive" /> This page is set to noindex — it won't appear in search.
              </div>
            )}
          </Card>

          <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
            <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Social share preview</h4>
            <div className="mt-4 overflow-hidden rounded-xl border border-border" data-testid="seo-social-preview">
              <div className="aspect-[1200/630] w-full bg-secondary/50">
                {imagePreview ? (
                  <img src={imagePreview} alt="Share preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                    <ImageIcon size={26} /><span className="text-xs">No share image yet</span>
                  </div>
                )}
              </div>
              <div className="space-y-1 border-t border-border bg-card p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{domain}</p>
                <p className="font-medium leading-snug line-clamp-1">{effSocialTitle}</p>
                <p className="text-sm text-muted-foreground leading-snug line-clamp-2">{effSocialDesc || "Add a social description."}</p>
              </div>
            </div>
          </Card>

          <a href={canonical} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="seo-view-live">
            <ExternalLink size={14} /> View live page
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Life Audit Reminders ---------------- */
function AuditReminders() {
  const [data, setData] = useState(null);
  const [statusF, setStatusF] = useState("");

  const load = useCallback(() => {
    api.get("/superadmin/audit-reminders")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load reminders."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };
  const summary = data?.summary;
  const rows = (data?.reminders || []).filter((r) => !statusF || r.status === statusF);

  const StatusBadge = ({ status }) => {
    if (status === "sent")
      return <Badge variant="secondary" className="text-muted-foreground" data-testid="reminder-status">Sent</Badge>;
    if (status === "due-soon")
      return <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]" data-testid="reminder-status">Due soon</Badge>;
    return <Badge variant="secondary" data-testid="reminder-status">Upcoming</Badge>;
  };

  const Stat = ({ label, value, icon: Icon, testid }) => (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        {Icon && <Icon size={15} className="text-[hsl(var(--gold))]" />}
      </div>
      <p className="mt-2 font-display text-3xl tabular-nums">{value ?? "—"}</p>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="audit-reminders">
      <div className="flex items-center gap-2">
        <CalendarClock size={20} className="text-[hsl(var(--gold))]" />
        <h3 className="font-display text-xl">Life Audit reminders</h3>
      </div>
      <p className="-mt-2 text-sm text-muted-foreground">
        A gentle "revisit your audit" email goes out ~90 days after someone completes the Life Audit. Here's who's coming up and who's already been nudged.
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Upcoming" value={summary?.upcoming} icon={CalendarClock} testid="reminders-stat-upcoming" />
        <Stat label="Due within 7 days" value={summary?.due_soon} icon={BellRing} testid="reminders-stat-duesoon" />
        <Stat label="Already sent" value={summary?.sent} icon={Check} testid="reminders-stat-sent" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["", "All"], ["due-soon", "Due soon"], ["upcoming", "Upcoming"], ["sent", "Sent"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatusF(v)} data-testid={`reminders-filter-${v || "all"}`}
            className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${statusF === v ? "bg-[hsl(var(--gold))] text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
        <Button variant="outline" size="sm" className="ml-auto rounded-lg" onClick={load} data-testid="reminders-refresh">
          <RefreshCw size={14} className="mr-1.5" />Refresh
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-[0.08em] text-muted-foreground">
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Email</th>
                <th className="p-3 text-right font-medium">Prev. score</th>
                <th className="p-3 font-medium">Tender pillar</th>
                <th className="p-3 font-medium">Scheduled</th>
                <th className="p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody data-testid="reminders-table-body">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground" data-testid="reminders-empty">
                  No reminders yet — they'll appear here as people complete the Life Audit.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid="reminder-row">
                  <td className="p-3 font-medium text-foreground">{r.name || "—"}</td>
                  <td className="p-3 text-muted-foreground">{r.email}</td>
                  <td className="p-3 text-right tabular-nums">{r.previous_score ?? "—"}</td>
                  <td className="p-3 text-muted-foreground">{r.tender_pillar || "—"}</td>
                  <td className="p-3 text-muted-foreground">{r.sent ? `sent ${fmtDate(r.sent_at)}` : fmtDate(r.due_at)}</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


/* ---------------- Page ---------------- */
export default function Superadmin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.is_master;

  useEffect(() => {
    if (!loading && user && !isMaster) navigate("/dashboard", { replace: true });
  }, [loading, user, isMaster, navigate]);

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!user) { navigate("/login", { replace: true }); return null; }
  if (!isMaster) return <div className="p-10 text-center text-muted-foreground">Not found.</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="superadmin-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--gold)/0.12)]"><ShieldAlert size={22} className="text-[hsl(var(--gold))]" /></div>
        <div>
          <h1 className="font-display text-3xl leading-tight">Superadmin Console</h1>
          <p className="text-sm text-muted-foreground">Master control — manage subscribers, content, and the whole system.</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="sa-tab-overview"><LayoutDashboard size={15} className="mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="bi" data-testid="sa-tab-bi"><BarChart3 size={15} className="mr-1.5" />Business Intelligence</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="sa-tab-subscribers"><Users size={15} className="mr-1.5" />Subscribers</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="sa-tab-newsletter"><Mail size={15} className="mr-1.5" />Newsletter</TabsTrigger>
          <TabsTrigger value="announcement" data-testid="sa-tab-announcement"><Megaphone size={15} className="mr-1.5" />Announcement</TabsTrigger>
          <TabsTrigger value="content" data-testid="sa-tab-content"><FileText size={15} className="mr-1.5" />Content</TabsTrigger>
          <TabsTrigger value="seo" data-testid="sa-tab-seo"><Globe size={15} className="mr-1.5" />SEO &amp; Sharing</TabsTrigger>
          <TabsTrigger value="audit" data-testid="sa-tab-audit"><ScrollText size={15} className="mr-1.5" />Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
        <TabsContent value="bi" className="mt-6">
          <Tabs defaultValue="bi-social">
            <TabsList>
              <TabsTrigger value="bi-social" data-testid="sa-bi-tab-social"><BarChart3 size={15} className="mr-1.5" />Social Media</TabsTrigger>
              <TabsTrigger value="bi-email" data-testid="sa-bi-tab-email"><Mail size={15} className="mr-1.5" />Email & Audience</TabsTrigger>
              <TabsTrigger value="bi-web" data-testid="sa-bi-tab-web"><Globe size={15} className="mr-1.5" />Website</TabsTrigger>
            </TabsList>
            <TabsContent value="bi-social" className="mt-6"><BISocial /></TabsContent>
            <TabsContent value="bi-email" className="mt-6"><BIEmail /></TabsContent>
            <TabsContent value="bi-web" className="mt-6"><BIWeb /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="subscribers" className="mt-6"><Subscribers /></TabsContent>
        <TabsContent value="newsletter" className="mt-6">
          <div className="space-y-10">
            <NewsletterSubs />
            <AuditReminders />
          </div>
        </TabsContent>
        <TabsContent value="announcement" className="mt-6"><Announcement /></TabsContent>
        <TabsContent value="content" className="mt-6"><Content /></TabsContent>
        <TabsContent value="seo" className="mt-6"><SeoManager /></TabsContent>
        <TabsContent value="audit" className="mt-6"><Audit /></TabsContent>
      </Tabs>
    </div>
  );
}
